import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import { collection } from 'utils/immutable';
import { useTranslation } from 'react-i18next';
import { getDateFormat, getDynamicFieldControlType } from 'utils/functions';
import { BackdropCustom, DialogButton, DialogTitle, SecondDialogButton } from './DocumentsStyles';
import { Dialog, TextField, Typography, DynamicField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';

const DocumentsUploadEditComponent = ({
  index,
  open,
  onCloseModal,
  onSave,
  onPropertyChange,
  document,
  loadPropertyDetails,
  saving,
  readOnlyFull,
  uploadDocumentWrite,
  validationInfo,
  dirtyInfo
}) => {
  const { t } = useTranslation();

  const onValidityDateChange = useCallback(
    value => {
      onPropertyChange('valabilityDate', value);
    },
    [onPropertyChange]
  );

  const handleParamValueChanged = useCallback(
    (key, value) => {
      const newCol = collection.update(key, x => ({ ...x, propertyValue: value }), document.attachmentProperties);
      onPropertyChange(`attachmentProperties`, newCol);
    },
    [document, onPropertyChange]
  );

  const onCommentsChange = useCallback(
    event => {
      onPropertyChange('comments', event);
    },
    [onPropertyChange]
  );

  return (
    <Dialog
      id='document-edit-dialog'
      open={open}
      onClose={onCloseModal}
      fullWidth
      maxWidth={'md'}
      BackdropComponent={BackdropCustom}
      transitionDuration={0}
      showX={false}
      disableBackdropClick
      disableEscapeKeyDown
      actionsProps={{ sx: theme => ({ px: theme.spacing(3), pb: theme.spacing(2) }) }}
      title={
        <Grid container mb={1}>
          <Grid item xs={12}>
            <DialogTitle>{document.attachmentTypeName}</DialogTitle>
          </Grid>
          <Grid item xs={12}>
            <Typography sx={theme => ({ fontSize: theme.spacing(2), fontWeight: '600' })}>
              {t('OfferEdit.Documents.Edit.Subtitle')}
            </Typography>
          </Grid>
        </Grid>
      }
      content={
        <Grid container spacing={4}>
          {document.canExpire && (
            <Grid item xs={12} sm={6}>
              <DateTime
                id={'DocumentAttachmentValidityDate_' + index}
                label={t('OfferEdit.Documents.Edit.ValidityDate')}
                value={document.valabilityDate}
                format={getDateFormat()}
                onChange={onValidityDateChange}
                isClearable
                error={Boolean(validationInfo.valabilityDate)}
                helperText={validationInfo.valabilityDate}
                disabled={
                  (document.attachmentId === saving.attachmentId && saving.inProgress) ||
                  document.isExternal ||
                  readOnlyFull ||
                  !uploadDocumentWrite
                }
                size={'medium'}
              />
            </Grid>
          )}
          {document?.attachmentProperties?.map((item, idx) => {
            if (!item.propertyVisible) {
              return '';
            } else if (item.propertyTypeCode === 'TP_DATE') {
              return (
                <Grid key={idx} item xs={12} sm={6}>
                  <DateTime
                    id={`[${index}]attachmentProperties[${idx}]`}
                    label={item.propertyName}
                    value={item.propertyValue}
                    format={getDateFormat()}
                    onChange={value => handleParamValueChanged(idx, value)}
                    error={validationInfo?.attachmentProperties && !validationInfo.attachmentProperties[idx]?.isValid}
                    helperText={validationInfo?.attachmentProperties && validationInfo.attachmentProperties[idx]?.propertyValue}
                    disabled={
                      item.propertyReadOnly ||
                      document.isExternal ||
                      (document.attachmentId === saving.attachmentId && saving.inProgress) ||
                      readOnlyFull ||
                      !uploadDocumentWrite
                    }
                    required={item.propertyMandatory}
                  />
                </Grid>
              );
            } else
              return (
                <Grid key={idx} item xs={12} sm={6}>
                  <DynamicField
                    id={`[${index}]attachmentProperties[${idx}]`}
                    label={item.propertyName}
                    value={item.propertyValue}
                    onChange={value => handleParamValueChanged(idx, value?.toString())}
                    loadOptions={() => loadPropertyDetails(item.propertyId)}
                    controlType={getDynamicFieldControlType(item.propertyTypeCode)}
                    error={validationInfo?.attachmentProperties && !validationInfo.attachmentProperties[idx]?.isValid}
                    helperText={validationInfo?.attachmentProperties && validationInfo.attachmentProperties[idx]?.propertyValue}
                    disabled={
                      item.propertyReadOnly ||
                      document.isExternal ||
                      (document.attachmentId === saving.attachmentId && saving.inProgress) ||
                      readOnlyFull ||
                      !uploadDocumentWrite
                    }
                    required={item.propertyMandatory}
                  />
                </Grid>
              );
          })}
          <Grid item xs={12}>
            <TextField
              id={'DocumentAttachmentsComments_' + index}
              fullWidth
              label={t('OfferEdit.Documents.Edit.Comments')}
              multiline
              maxRows='4'
              value={document.comments || ''}
              onChange={onCommentsChange}
              margin='normal'
              error={Boolean(validationInfo.comments)}
              helperText={validationInfo.comments}
              disabled={
                (document.attachmentId === saving.attachmentId && saving.inProgress) ||
                document.isExternal ||
                readOnlyFull ||
                !uploadDocumentWrite
              }
              sx={{ margin: 0 }}
            />
          </Grid>
        </Grid>
      }
      actions={
        <>
          <DialogButton color='primary' variant='outlined' onClick={onCloseModal}>
            {t('OfferEdit.Documents.Edit.Cancel')}
          </DialogButton>
          <SecondDialogButton
            color='primary'
            variant='contained'
            onClick={onSave}
            disabled={
              (document.attachmentId === saving.attachmentId && saving.inProgress) ||
              document.isExternal ||
              !dirtyInfo.isDirty ||
              !validationInfo.isValid
            }
          >
            {t('OfferEdit.Documents.Edit.Save')}
          </SecondDialogButton>
        </>
      }
    />
  );
};

DocumentsUploadEditComponent.propTypes = {
  index: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  document: PropTypes.object.isRequired,
  loadPropertyDetails: PropTypes.func,
  saving: PropTypes.object,
  readOnlyFull: PropTypes.bool.isRequired,
  uploadDocumentWrite: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  dirtyInfo: PropTypes.object.isRequired
};

export default DocumentsUploadEditComponent;
